import { Controller } from "@hotwired/stimulus";
import { seemsOffline } from "../../../../utils/connectivity";
import { isPwaEnabled } from "../../../../utils/offline_gating";

export default class extends Controller {
  static targets = [
    "step",
    "heading",
    "stepStatus",
    "todoAutoPanel",
    "submitButton",
    "autoAddTodosField",
    "todoListIdsField",
    "newTodoListField",
    "todoListDropdownInput",
  ];

  static values = {
    totalSteps: { type: Number, default: 8 },
    currentStep: { type: Number, default: 1 },
  };

  connect() {
    this.showStep(this.currentStepValue);

    if (!isPwaEnabled()) return;

    this.boundEnforceConnectivity = () => this.redirectIfOffline();
    this.boundBlockSubmit = (event) => {
      if (!this.redirectIfOffline()) return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("offline", this.boundEnforceConnectivity);
    this.element.addEventListener("submit", this.boundBlockSubmit, true);
    this.redirectIfOffline();
  }

  disconnect() {
    if (this.boundEnforceConnectivity) {
      window.removeEventListener("offline", this.boundEnforceConnectivity);
    }
    if (this.boundBlockSubmit) {
      this.element.removeEventListener("submit", this.boundBlockSubmit, true);
    }
  }

  redirectIfOffline() {
    if (!isPwaEnabled() || !seemsOffline()) return false;
    const modal = document.querySelector(
      '[data-offline-navigation-target="featureUnavailableModal"]',
    );
    if (modal) {
      modal.classList.remove("hidden");
      modal.classList.add("gen-modal--front");
    }
    return true;
  }

  next(event) {
    event?.preventDefault();
    if (this.redirectIfOffline()) return;
    if (this.currentStepValue >= this.totalStepsValue) return;
    this.showStep(this.currentStepValue + 1);
  }

  previous(event) {
    event?.preventDefault();
    if (this.redirectIfOffline()) return;
    if (this.currentStepValue <= 1) return;
    if (this.currentStepValue === 8) {
      this.showStep(6);
      return;
    }
    this.showStep(this.currentStepValue - 1);
  }

  skipToTodo(event) {
    event?.preventDefault();
    if (this.redirectIfOffline()) return;
    this.showStep(7);
    this.clearTodoPreferenceSelection();
    this.focusActiveHeading();
  }

  showStep(stepNumber) {
    const previousStep = this.currentStepValue;
    this.currentStepValue = stepNumber;

    this.stepTargets.forEach((step) => {
      const stepIndex = Number(step.dataset.stepIndex);
      const active = stepIndex === stepNumber;
      step.hidden = !active;
      if (active) {
        step.removeAttribute("inert");
      } else {
        step.setAttribute("inert", "");
      }
      this.syncStepFieldDisabledState(step, active);
    });

    if (stepNumber === 7 && previousStep === 6) {
      this.clearTodoPreferenceSelection();
    }

    this.updateStepAnnouncement();
    this.manageStepFocus(stepNumber, previousStep);
    this.updateSubmitButtons();
  }

  manageStepFocus(stepNumber, previousStep) {
    if (stepNumber === previousStep) return;

    const enteringTodoFromTour = stepNumber === 7 && previousStep === 6;
    if (enteringTodoFromTour || (stepNumber !== 7 && stepNumber !== 8)) {
      this.focusActiveHeading();
    }
  }

  syncStepFieldDisabledState(step, active) {
    step
      .querySelectorAll("input, select, textarea, button")
      .forEach((control) => {
        if (control.type === "hidden") return;
        control.disabled = !active;
      });
  }

  updateStepAnnouncement() {
    if (!this.hasStepStatusTarget) return;
    this.stepStatusTarget.textContent = `Step ${this.currentStepValue} of ${this.totalStepsValue}`;
  }

  focusActiveHeading() {
    const activeStep = this.stepTargets.find((step) => !step.hidden);
    const heading =
      activeStep?.querySelector(
        '[data-quiz-player--calibrate-orientation-target="heading"]',
      ) || this.headingTargets.find((node) => activeStep?.contains(node));

    if (!heading) return;
    heading.setAttribute("tabindex", "-1");
    heading.focus({ preventScroll: true });
  }

  focusActiveTodoPreference() {
    const activeStep = this.activeTodoPreferenceStep();
    const checked = activeStep?.querySelector(
      'input[name="auto_add_todos"]:checked',
    );
    if (!checked || checked.disabled) return;

    checked.focus({ preventScroll: true });
  }

  toggleTodoMode(event) {
    if (this.redirectIfOffline()) return;

    const autoAdd = event.target.value === "true";
    if (this.currentStepValue === 7 || this.currentStepValue === 8) {
      this.showStep(autoAdd ? 7 : 8);
    }
    this.selectTodoMode(autoAdd);
    if (this.currentStepValue === 7 || this.currentStepValue === 8) {
      this.focusActiveTodoPreference();
    }
  }

  clearTodoPreferenceSelection() {
    this.stepTargets.forEach((step) => {
      step.querySelectorAll('input[name="auto_add_todos"]').forEach((input) => {
        input.checked = false;
      });
    });

    if (this.hasAutoAddTodosFieldTarget) {
      this.autoAddTodosFieldTarget.value = "false";
    }
    this.updateTodoAutoPanelVisibility();
    this.syncTodoListDropdown();
    this.updateSubmitButtons();
  }

  activeTodoPreferenceStep() {
    return this.stepTargets.find((step) => !step.hidden);
  }

  selectTodoMode(autoAdd) {
    const activeStep = this.activeTodoPreferenceStep();

    this.stepTargets.forEach((step) => {
      step.querySelectorAll('input[name="auto_add_todos"]').forEach((input) => {
        if (step !== activeStep) {
          input.checked = false;
          return;
        }
        input.checked = input.value === (autoAdd ? "true" : "false");
      });
    });

    if (this.hasAutoAddTodosFieldTarget) {
      this.autoAddTodosFieldTarget.value = autoAdd ? "true" : "false";
    }
    this.updateTodoAutoPanelVisibility();
    this.syncTodoListDropdown();
    this.updateSubmitButtons();
  }

  updateTodoAutoPanelVisibility() {
    if (!this.hasTodoAutoPanelTarget) return;

    const activeStep = this.activeTodoPreferenceStep();
    const autoRadio = activeStep?.querySelector(
      'input[name="auto_add_todos"][value="true"]',
    );
    const autoSelected = autoRadio?.checked === true;
    this.todoAutoPanelTarget.hidden = !autoSelected;

    if (this.hasAutoAddTodosFieldTarget) {
      this.autoAddTodosFieldTarget.value = autoSelected ? "true" : "false";
    }
  }

  syncTodoListDropdown() {
    if (this.redirectIfOffline()) return;

    if (this.autoAddTodosFieldTarget?.value !== "true") {
      if (this.hasTodoListIdsFieldTarget) {
        this.todoListIdsFieldTarget.value = "";
      }
      if (this.hasNewTodoListFieldTarget) {
        this.newTodoListFieldTarget.value = "";
      }
      this.updateSubmitButtons();
      return;
    }

    const dropdownValue = this.hasTodoListDropdownInputTarget
      ? this.todoListDropdownInputTarget.value
      : "";
    const values = dropdownValue
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

    const listIds = [];
    const createdNames = [];

    values.forEach((value) => {
      const row = this.dropdownRowForValue(value);
      if (row?.dataset.created === "true") {
        const label =
          row.dataset.label?.trim() ||
          row
            .querySelector(".dropdown-menu-checkbox-label")
            ?.textContent?.trim() ||
          value;
        createdNames.push(label);
        return;
      }

      listIds.push(value);
    });

    // ponytail: update_todos_settings accepts one new_todo_list_name; last created wins when several are selected.
    const newListName = createdNames.at(-1) || "";

    if (this.hasTodoListIdsFieldTarget) {
      this.todoListIdsFieldTarget.value = listIds.join(",");
    }
    if (this.hasNewTodoListFieldTarget) {
      this.newTodoListFieldTarget.value = newListName;
    }
    this.updateSubmitButtons();
  }

  dropdownRowForValue(value) {
    if (!value) return null;

    const escaped =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(value)
        : value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

    return this.element.querySelector(
      `[data-railsui-dropdown-menu-target~="row"][data-value="${escaped}"]`,
    );
  }

  updateSubmitButtons() {
    if (!this.hasSubmitButtonTarget) return;

    const activeStep = this.activeTodoPreferenceStep();
    const preferenceSelected = Boolean(
      activeStep?.querySelector('input[name="auto_add_todos"]:checked'),
    );
    if (
      (this.currentStepValue === 7 || this.currentStepValue === 8) &&
      !preferenceSelected
    ) {
      this.submitButtonTargets.forEach((button) => {
        button.disabled = true;
      });
      return;
    }

    const autoAdd = this.autoAddTodosFieldTarget?.value === "true";
    if (!autoAdd) {
      this.submitButtonTargets.forEach((button) => {
        button.disabled = false;
      });
      return;
    }

    const listIds =
      this.todoListIdsFieldTarget?.value
        ?.split(",")
        .map((entry) => entry.trim())
        .filter(Boolean) || [];
    const newListName = this.newTodoListFieldTarget?.value.trim() || "";
    const disabled = listIds.length === 0 && newListName === "";
    this.submitButtonTargets.forEach((button) => {
      button.disabled = disabled;
    });
  }
}
